import React from "react";
import { Stack, Box, Typography, alpha } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch } from "../redux/store";
import { openShift } from "../redux/actions/shiftActions";
import { cleanFormData } from "../helpers/formHelpers";
import { shiftActions } from "../redux/slices/shift";
import { ShiftOpenRequestDto } from "../shared/dtos/shift/ShiftOpenRequestDto";

const openShiftSchema = Yup.object().shape({
  openingFloat: Yup.number()
    .required("Opening float is required")
    .min(0, "Float cannot be negative")
    .typeError("Must be a valid number"),
});

const OpenShiftForm: React.FC<{ loading: boolean; error: string | null }> = ({
  loading = false,
  error = null,
}): React.JSX.Element => {
  const dispatch = useAppDispatch();

  const initialValues = {
    openingFloat: "" as unknown as number,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(shiftActions.setErrorOpen(null))}
      />

      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 1,
          bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
          border: "1px solid",
          borderColor: "info.light",
        }}
      >
        <Typography variant="body2" color="info.main" sx={{ fontWeight: 500 }}>
          Enter the starting cash amount available in the drawer to begin your
          session.
        </Typography>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={openShiftSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(values);
          dispatch(openShift(payload));
        }}
      >
        {({ dirty, isValid }: FormikProps<ShiftOpenRequestDto>) => (
          <Form>
            <Stack spacing={3}>
              <FormTextField
                name="openingFloat"
                label="Opening Float (Cash Balance)"
                type="number"
                placeholder="0.00"
                autoFocus
              />

              <SubmitButton
                title="Start Business Shift"
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={<LockOpenIcon />}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default OpenShiftForm;
